/**
 * Defensive guard for DOM mutations React makes against nodes that something
 * outside React has already moved.
 *
 * WHY THIS EXISTS
 * ---------------
 * `NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be
 * removed is not a child of this node.` was the single largest genuine crash
 * class in the product (111 reports, v1.1.22 → v1.1.30). The telemetry is
 * unambiguous about the cause — of 80 reports carrying full metadata:
 *
 *   - 79 Microsoft Edge + 1 Firefox, 100% desktop, 100% `scope: "route"`
 *   - browser language: de 47, cs 17, hu 12, nl/sv/fi/fr 1 each — ZERO English
 *
 * against a baseline of 17,729 other auto-reports that are 83.8% English and
 * only 2.6% Edge. Edge is ~38x over-represented here, and English — five sixths
 * of the baseline — is completely absent. That combination has one explanation:
 * the browser's BUILT-IN PAGE TRANSLATOR, which Edge and Firefox offer
 * automatically when the page language differs from the browser language.
 *
 * The translator rewrites text in place: it detaches a text node, wraps it in
 * its own element (`<font>`), and re-inserts that. React's fiber tree still
 * holds the original text node and still believes its parent is the original
 * element. On the next unmount React calls `originalParent.removeChild(text)`,
 * the DOM disagrees about parentage, and the whole route boundary crashes.
 *
 * WHY THE GUARD AND NOT A PER-COMPONENT FIX
 * -----------------------------------------
 * The usual advice is "wrap bare conditional text nodes in a `<span>` so React
 * only ever removes elements it still owns". That does not work here. 77 of the
 * 80 reports share one component stack — `span > h5 > div > div > span`, which
 * maps exactly to the typing-label branch of `ChatListEntry` (the outer `span`
 * is Radix's `ContextMenuTrigger`) — and that text is ALREADY wrapped in a
 * `<span>`. The translator reparents the text inside the wrapper too, so the
 * wrapper buys nothing. A guard at the DOM boundary is the only mitigation that
 * covers every route, every component, and every future one.
 *
 * WHAT IT DOES
 * ------------
 * It makes the two mutations React performs against a stale parent reference
 * degrade instead of throw, exactly at the point where the DOM spec would raise
 * `NotFoundError`:
 *
 *   - `removeChild(child)` where `child` is not ours — the node is already gone
 *     from this parent, which is the outcome React wanted. No-op and return it.
 *   - `insertBefore(node, ref)` where `ref` is not ours — append instead, so the
 *     node still lands in the right parent. Sibling order may be briefly wrong
 *     until React's next full render; a crash screen is far worse.
 *
 * Every interception is reported under the distinct `"dom-guard"` source so
 * this can never silently paper over a genuine React bug: the reports stay
 * filterable, carry a stack trace pointing at the real caller, and pass through
 * the same dedupe/rate-limit gate as every other report.
 *
 * PRIVACY: ORION is an E2E-encrypted messenger, so the nodes involved routinely
 * contain decrypted message previews, chat names, and usernames. Reports here
 * emit NODE NAMES ONLY (`<h5>`, `<#text>`). Never `nodeValue`, `textContent`,
 * `outerHTML`, or attributes.
 */

import { captureError } from "./capture";
import type { ErrorSource } from "./reportError";

const GUARD_SOURCE: ErrorSource = "dom-guard";

let installed = false;
let originalRemoveChild: typeof Node.prototype.removeChild | null = null;
let originalInsertBefore: typeof Node.prototype.insertBefore | null = null;

/** Node NAME only — never any content. See the PRIVACY note above. */
function nodeName(node: Node | null): string {
  if (!node) {
    return "none";
  }
  try {
    return `<${node.nodeName.toLowerCase()}>`;
  } catch {
    return "<unknown>";
  }
}

/**
 * Reports one interception. Must never throw: it runs inside a DOM primitive
 * that the whole app (and React's commit phase) depends on.
 */
function reportGuardHit(
  operation: "removeChild" | "insertBefore",
  parent: Node,
  child: Node,
  // Frames above this function are dropped from the stack (V8 only) so the
  // first frame is React's actual caller. dedupe.ts fingerprints on the first
  // frame, so without this every interception in the app would collapse into a
  // single fingerprint and we would lose per-call-site visibility.
  topOfStack: (...args: never[]) => unknown,
): void {
  try {
    const error = new Error(
      `${operation}: node is not a child of the given parent ` +
        `(parent=${nodeName(parent)}, node=${nodeName(child)}, ` +
        `actualParent=${nodeName(child.parentNode)}). ` +
        `Recovered without throwing; usually a browser page translator ` +
        `reparenting nodes behind React.`,
    );
    error.name = "DomGuardParentMismatch";
    (
      Error as unknown as {
        captureStackTrace?: (
          target: object,
          constructorOpt?: (...args: never[]) => unknown,
        ) => void;
      }
    ).captureStackTrace?.(error, topOfStack);
    captureError({ error, scope: "root", source: GUARD_SOURCE });
  } catch {
    // Reporting must never become a second failure inside a DOM primitive.
  }
}

/**
 * Patches `Node.prototype.removeChild` / `insertBefore`. Idempotent, and a
 * no-op outside a DOM environment. Call once, as early as possible — before
 * React renders.
 */
export function installDomGuard(): void {
  if (installed || typeof Node === "undefined") {
    return;
  }
  installed = true;

  originalRemoveChild = Node.prototype.removeChild;
  originalInsertBefore = Node.prototype.insertBefore;

  function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    // `child instanceof Node` keeps the native TypeError for non-Node
    // arguments: we only want to intervene in the spec's NotFoundError case.
    if (child instanceof Node && child.parentNode !== this) {
      reportGuardHit("removeChild", this, child, guardedRemoveChild);
      return child;
    }
    return originalRemoveChild!.call(this, child) as T;
  }

  function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    referenceNode: Node | null,
  ): T {
    if (
      referenceNode instanceof Node &&
      referenceNode.parentNode !== this &&
      node instanceof Node
    ) {
      reportGuardHit("insertBefore", this, referenceNode, guardedInsertBefore);
      // A null reference means "append". Go through the ORIGINAL rather than
      // appendChild so we never re-enter a patched primitive.
      return originalInsertBefore!.call(this, node, null) as T;
    }
    return originalInsertBefore!.call(this, node, referenceNode) as T;
  }

  Node.prototype.removeChild =
    guardedRemoveChild as typeof Node.prototype.removeChild;
  Node.prototype.insertBefore =
    guardedInsertBefore as typeof Node.prototype.insertBefore;
}

/**
 * Restores the native primitives.
 *
 * Test-only, mirroring `resetDedupeState` in dedupe.ts: the regression suite
 * has to run the same translator scenario with the guard OFF (to prove the
 * crash is real) and ON (to prove it is fixed). Nothing in the app uninstalls.
 */
export function uninstallDomGuard(): void {
  if (!installed) {
    return;
  }
  if (originalRemoveChild) {
    Node.prototype.removeChild = originalRemoveChild;
  }
  if (originalInsertBefore) {
    Node.prototype.insertBefore = originalInsertBefore;
  }
  originalRemoveChild = null;
  originalInsertBefore = null;
  installed = false;
}
